import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, LifeBuoy, RefreshCw } from 'lucide-react';
import type { ClientProfile } from '@his/shared';
import { get } from '@/lib/api';
import { dateOnly } from '@/lib/utils';
import { EmptyState, PageHeader, StatTile } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, SkeletonText, StatusBadge } from '@/components/ui/primitives';

/** Below this, the renewal button stops being a link and starts being the point. */
const EXPIRY_WARNING_DAYS = 30;

/**
 * The contact person's home: the hospitals they look after, one card each.
 *
 * Every figure here is commercial — when access runs out, how many tickets are
 * open. Nothing clinical appears, and nothing clinical could: a platform token
 * reaches no patient record anywhere in this system.
 */
export function ClientHomePage() {
  const navigate = useNavigate();
  const profile = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => get<ClientProfile>('/client/me'),
  });

  if (profile.isLoading) {
    return (
      <SkeletonText lines={3} />
    );
  }

  const labs = profile.data?.labs ?? [];
  const expiringSoon = labs.filter(
    (lab) => lab.daysRemaining !== null && lab.daysRemaining <= EXPIRY_WARNING_DAYS,
  );
  const openTickets = labs.reduce((sum, lab) => sum + lab.openTickets, 0);

  return (
    <>
      <PageHeader
        title={`Hello, ${profile.data?.name ?? ''}`}
        description="The hospitals you look after"
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label="Hospitals" value={labs.length} />
        <StatTile label="Open tickets" value={openTickets} tone={openTickets ? 'warning' : undefined} />
        <StatTile
          label="Expiring soon"
          value={expiringSoon.length}
          tone={expiringSoon.length ? 'warning' : undefined}
          sub={`within ${EXPIRY_WARNING_DAYS} days`}
        />
      </div>

      {labs.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No hospitals assigned yet"
          description="Once the owner names you as the contact for a hospital, it will appear here."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {labs.map((lab) => {
            const urgent = lab.daysRemaining !== null && lab.daysRemaining <= EXPIRY_WARNING_DAYS;
            return (
              <Card key={lab.tenantId}>
                <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
                  <div className="min-w-0">
                    <CardTitle className="text-base">{lab.name}</CardTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {lab.slug}
                      {lab.planName ? ` · ${lab.planName}` : ''}
                    </p>
                  </div>
                  <StatusBadge status={lab.status} tone={lab.status === 'active' ? 'success' : 'warning'} />
                </CardHeader>

                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="text-muted-foreground">
                      Access runs to{' '}
                      <span className={urgent ? 'font-medium text-warning' : 'font-medium text-foreground'}>
                        {lab.subscriptionEndsAt ? dateOnly(lab.subscriptionEndsAt) : 'no date set'}
                      </span>
                    </span>
                    {lab.daysRemaining !== null && (
                      <span className={urgent ? 'text-warning' : 'text-muted-foreground'}>
                        {lab.daysRemaining >= 0 ? `${lab.daysRemaining} days left` : 'Expired'}
                      </span>
                    )}
                  </div>

                  {lab.readOnly && (
                    <Alert variant="warning" className="text-xs">
                      <AlertTriangle className="me-1 inline h-3.5 w-3.5" />
                      This hospital is read-only until the subscription is renewed. Records stay
                      readable — nobody is locked out of a chart.
                    </Alert>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={urgent ? 'default' : 'secondary'}
                      size="sm"
                      onClick={() => navigate(`/client/renewals/new?tenantId=${lab.tenantId}`)}
                    >
                      <RefreshCw className="me-1.5 h-4 w-4" />
                      {lab.pendingRenewal ? 'Renewal pending' : 'Renew subscription'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/client/tickets?tenantId=${lab.tenantId}`)}
                    >
                      <LifeBuoy className="me-1.5 h-4 w-4" />
                      Tickets{lab.openTickets ? ` (${lab.openTickets})` : ''}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        Need something else? <Link className="underline" to="/client/tickets">Open a ticket</Link>.
      </p>
    </>
  );
}
